import { useEffect, useState } from 'react'
import { fetchLeaderboard, type LeaderboardEntry } from '../api'
import { Header } from '../components/Header'
import {
  DISCIPLINE_LABELS,
  SPRINT_DISTANCES,
  variantLabel,
  type Discipline,
  type SprintDistanceId,
} from '../engine/disciplines'
import { loadProfile } from '../engine/records'
import { formatDistanceInline, formatTime } from '../engine/units'

const DISCIPLINES: Discipline[] = ['langstrecke', 'sprung', 'sprint']

export function BestenlisteScreen() {
  const [discipline, setDiscipline] = useState<Discipline>('sprung')
  const [variant, setVariant] = useState<SprintDistanceId>('100m')
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ownName] = useState(() => loadProfile().name.trim())

  const activeVariant = discipline === 'sprint' ? variant : null

  useEffect(() => {
    let cancelled = false
    setEntries(null)
    setError(null)

    void fetchLeaderboard(discipline, activeVariant).then((response) => {
      if (cancelled) return
      if (response.ok) setEntries(response.data)
      else setError(response.error)
    })

    return () => {
      cancelled = true
    }
  }, [discipline, activeVariant])

  const value = (entry: LeaderboardEntry): string =>
    discipline === 'sprint' ? formatTime(entry.timeMs) : formatDistanceInline(entry.distanceCm)

  // Die ersten drei bekommen ein Treppchen, der Rest eine Liste. Ein Podest
  // macht auf einen Blick klar, worum gespielt wird - eine reine Tabelle nicht.
  const podium = entries?.slice(0, 3) ?? []
  const rest = entries?.slice(3) ?? []

  return (
    <div className="screen screen--page" data-discipline={discipline}>
      <Header title="Bestenliste" />
      <div className="page">
        <div className="tabs">
          {DISCIPLINES.map((entry) => (
            <button
              key={entry}
              type="button"
              className={entry === discipline ? 'tab tab--active' : 'tab'}
              onClick={() => setDiscipline(entry)}
            >
              {DISCIPLINE_LABELS[entry]}
            </button>
          ))}
        </div>

        {discipline === 'sprint' && (
          <div className="tabs tabs--sub">
            {SPRINT_DISTANCES.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={entry.id === variant ? 'tab tab--active' : 'tab'}
                onClick={() => setVariant(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>
        )}

        {error && <p className="message message--error">{error}</p>}
        {!error && entries === null && <p className="page__note">Wird geladen…</p>}
        {entries?.length === 0 && (
          <div className="empty">
            <p className="empty__title">Noch niemand hier</p>
            <p className="empty__text">Der erste Platz ist frei. Trag dich ein.</p>
          </div>
        )}

        {podium.length > 0 && (
          <ol className="podium">
            {podium.map((entry) => (
              <li
                key={`${entry.rank}-${entry.playerName}`}
                className={`podium__place podium__place--${entry.rank}`}
                data-own={entry.playerName === ownName ? 'true' : undefined}
              >
                <span className="podium__rank">{entry.rank}</span>
                <span className="podium__name">{entry.playerName}</span>
                <span className="podium__value">{value(entry)}</span>
              </li>
            ))}
          </ol>
        )}

        {rest.length > 0 && (
          <ol className="ranking">
            {rest.map((entry) => (
              <li
                key={`${entry.rank}-${entry.playerName}`}
                className="ranking__row"
                data-own={entry.playerName === ownName ? 'true' : undefined}
              >
                <span className="ranking__rank">{entry.rank}</span>
                <span className="ranking__name">{entry.playerName}</span>
                <span className="ranking__value">{value(entry)}</span>
              </li>
            ))}
          </ol>
        )}

        {entries && entries.length > 0 && (
          <p className="meta">
            Je Gerät zählt der beste Versuch. {DISCIPLINE_LABELS[discipline]}
            {activeVariant ? ` · ${variantLabel(activeVariant)}` : ''}
          </p>
        )}
      </div>
    </div>
  )
}
